import React, { useState } from 'react';
import { FiAlertCircle, FiSliders, FiArrowRight, FiPlus, FiUser, FiX } from 'react-icons/fi';
import MegaphoneIcon from '../../MegaphoneIcon';
import Toggle from '../../Toggle';
import { toast } from '../../Toast';
import { RuleSet, getFilterWords, getFilterPattern } from './announcementData';
import { SectionProps } from './AnnouncementSettings';
import StandardFilterModal from './StandardFilterModal';

const RULES: { key: keyof RuleSet; label: string }[] = [
    { key: 'email', label: 'Email subscribers automatically' },
    { key: 'confirm', label: 'Send confirmation to approvers' },
];

const EMAIL = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

const Rules: React.FC<SectionProps> = ({ form, setForm }) => {
    const [adding, setAdding] = useState(false);
    const [word, setWord] = useState('');
    const [approver, setApprover] = useState('');
    const [standard, setStandard] = useState(false);
    // a filter that isn't a plain word list can only be edited as a pattern
    const words = getFilterWords(form.filter);
    const [asPattern, setAsPattern] = useState(false);
    const showPattern = asPattern || words === null;

    const setRule = (group: 'priceSensitive' | 'other', key: keyof RuleSet, value: boolean) =>
        setForm(f => ({ ...f, [group]: { ...f[group], [key]: value } }));

    const setWords = (next: string[]) => setForm(f => ({ ...f, filter: getFilterPattern(next) }));

    const addWords = (added: string[]) => {
        const current = words ?? [];
        setWords([...current, ...added.filter(w => !current.some(x => x.toLowerCase() === w.toLowerCase()))]);
    };

    const addWord = () => {
        const w = word.trim();
        if (w) addWords([w]);
        setWord('');
        setAdding(false);
    };

    // typed or pasted, one or more addresses separated by commas, semicolons or spaces (v1 ConfirmationEmail)
    const addApprovers = () => {
        const typed = approver.split(/[,;\s]+/).map(x => x.trim()).filter(x => x);
        if (typed.length === 0) return;
        const bad = typed.find(x => !EMAIL.test(x));
        if (bad) {
            toast.error(`${bad} isn’t an email address`);
            return;
        }
        setForm(f => ({ ...f, approvers: [...f.approvers, ...typed.filter((x, i) => typed.indexOf(x) === i && !f.approvers.some(a => a.toLowerCase() === x.toLowerCase()))] }));
        setApprover('');
    };

    const group = (key: 'priceSensitive' | 'other', title: string) =>
        <div className={`rule-group ${key}`}>
            <div className="head"><span className="icon-tile"><FiAlertCircle /></span><strong>{title}</strong></div>
            {RULES.map(r => <div key={r.key} className="rule">
                <span className="label">{r.label}</span>
                <span className={`yes-no${form[key][r.key] ? ' on' : ''}`}>{form[key][r.key] ? 'Yes' : 'No'}</span>
                <Toggle label={`${title}: ${r.label}`} checked={form[key][r.key]} onChange={v => setRule(key, r.key, v)} />
            </div>)}
        </div>;

    const standardLink = (cls: string) => <button type="button" className={`link ${cls}`} onClick={() => setStandard(true)}>Insert Standard Filter <FiArrowRight /></button>;
    const modeLink = (cls: string) => showPattern
        ? words !== null && <button type="button" className={`link ${cls}`} onClick={() => setAsPattern(false)}>Edit as words <FiArrowRight /></button>
        : <button type="button" className={`link ${cls}`} onClick={() => setAsPattern(true)}>Edit as a pattern (advanced) <FiArrowRight /></button>;

    return <>
        <section className="card">
            <div className="section-title">
                <span className="icon-tile"><MegaphoneIcon /></span>
                <div className="text">
                    <h2>When an Announcement Arrives</h2>
                    <div className="hint">Choose which announcements are emailed automatically and which wait for an Approver.</div>
                </div>
            </div>
            <div className="rule-groups">
                {group('priceSensitive', 'Price sensitive')}
                {group('other', 'Not price sensitive')}
            </div>
        </section>

        <section className="card">
            <div className="section-title">
                <span className="icon-tile"><FiSliders /></span>
                <div className="text">
                    <h2>Don't Auto‑Send These</h2>
                    <div className="hint">{showPattern
                        ? 'Announcements whose headline matches this pattern (a regular expression, ignoring case) are never emailed automatically'
                        : 'Announcements whose headline matches a filter are never emailed automatically'}</div>
                </div>
                <div className="title-links">
                    {!showPattern && standardLink('in-title')}
                    {modeLink('in-title')}
                </div>
            </div>
            {showPattern
                ? <input className="pattern-input" value={form.filter} placeholder="e.g. appendix.*(3b|3y)" spellCheck={false}
                    onChange={e => { const filter = e.target.value; setForm(f => ({ ...f, filter })); }} />
                : <div className="chips-wrap">
                    {words!.map(w => <span key={w} className="chip">
                        {w}
                        <button type="button" aria-label={`Remove ${w}`} onClick={() => setWords(words!.filter(x => x !== w))}><FiX /></button>
                    </span>)}
                    {adding
                        ? <input className="chip-input" autoFocus value={word} placeholder="Word or phrase" onChange={e => setWord(e.target.value)}
                            onBlur={addWord} onKeyDown={e => { if (e.key === 'Enter') addWord(); if (e.key === 'Escape') { setWord(''); setAdding(false); } }} />
                        : <button type="button" className="chip add" onClick={() => setAdding(true)}><FiPlus /> Add a word or phrase</button>}
                </div>}
            {!showPattern && standardLink('at-bottom')}
            {modeLink('at-bottom')}
        </section>

        <section className="card">
            <div className="section-title"><span className="icon-tile"><FiUser /></span><h2>Who Approves and Gets Confirmations</h2></div>
            <div className="tag-input">
                {form.approvers.length > 0 && <div className="tags">
                    {form.approvers.map(a => <span key={a} className="tag">
                        <span className="who">{a}</span>
                        <button type="button" aria-label={`Remove ${a}`} onClick={() => setForm(f => ({ ...f, approvers: f.approvers.filter(x => x !== a) }))}><FiX /></button>
                    </span>)}
                </div>}
                <input value={approver} placeholder="Add an email address…" onChange={e => setApprover(e.target.value)} onBlur={addApprovers}
                    onKeyDown={e => { if (e.key === 'Enter' || e.key === ',') { e.preventDefault(); addApprovers(); } }} />
            </div>
        </section>

        {standard && <StandardFilterModal existing={words ?? []} onClose={() => setStandard(false)}
            onAdd={added => { addWords(added); setStandard(false); }} />}
    </>;
};

export default Rules;
